import { useAppSelector } from 'app/hooks';
import { ConfigurationType, SystemOptionType } from 'data/types';
import {
  ServerFilterOptionCountsType,
  ServerFilterUniverseType
} from 'features/systems';
import useFetchedConfigurations from 'hooks/use-fetched-configurations';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { useMemo } from 'react';
import { combinationToSystemOption } from 'utils/valid-systems';
import {
  buildCondenserSeriesLabels,
  buildFilterUniverse,
  mergeFilterUniverses,
  SystemListFilterState,
  SystemListFilterUniverse
} from './equipment-comparison-filters';
import { reconcileConfigIds } from './equipment-filter-conversion';
import useSystemListFilterState from './use-system-list-filter-state';

type EquipmentFiltersModel = {
  allSystemOptions: SystemOptionType[];
  universe: SystemListFilterUniverse;
  filterState: SystemListFilterState;
  setFilterState: (next: SystemListFilterState) => void;
  condenserSeriesLabels: Record<string, string>;
  optionCounts: ServerFilterOptionCountsType;
};

// Per-config seeding: the server universe (derived from the UNFILTERED pool)
// is preferred when present; only configs without one fall back to the local
// universe built from that config's loaded options. There is deliberately no
// union of server + local within one config — the server universe supersedes
// whatever subset of pages happens to be loaded locally.
export function buildEffectiveRawUniverse(
  selectedConfigIds: string[] | null,
  optionsByConfig: Map<string, SystemOptionType[]>,
  serverUniverseByConfig: Record<string, ServerFilterUniverseType>
): SystemListFilterUniverse {
  const allConfigIds = new Set<string>([
    ...Array.from(optionsByConfig.keys()),
    ...Object.keys(serverUniverseByConfig)
  ]);
  const selected =
    selectedConfigIds === null ? null : new Set(selectedConfigIds);

  const perConfig: SystemListFilterUniverse[] = [];
  allConfigIds.forEach((configId) => {
    if (selected && !selected.has(configId)) return;
    const server = serverUniverseByConfig[configId];
    if (server) {
      perConfig.push({
        airHandlerTypes: server.airHandlerTypes,
        condenserSeries: server.condenserSeries,
        condenserBtus: server.condenserBtus,
        hyperHeat: server.hyperHeat,
        systemTypes: server.systemTypes,
        refrigerants: server.refrigerants
      });
    } else {
      perConfig.push(buildFilterUniverse(optionsByConfig.get(configId) ?? []));
    }
  });
  return mergeFilterUniverses(perConfig);
}

const COUNT_DIMENSIONS = [
  'airHandlerTypes',
  'condenserSeries',
  'condenserBtus',
  'hyperHeat',
  'systemTypes',
  'refrigerants'
] as const;

// What-if option counts merged across CHECKED configs: for each dimension,
// merged count for option O = sum of counts over checked configs where O's
// key is present. Unlike the universe there is no client-side computation to
// fall back to — configs without server counts contribute nothing, and if no
// checked config has counts every Record is empty (the UI renders no numbers).
export function mergeFilterOptionCounts(
  selectedConfigIds: string[] | null,
  countsByConfig: Record<string, ServerFilterOptionCountsType>
): ServerFilterOptionCountsType {
  const selected =
    selectedConfigIds === null ? null : new Set(selectedConfigIds);
  const merged: ServerFilterOptionCountsType = {
    airHandlerTypes: {},
    condenserSeries: {},
    condenserBtus: {},
    hyperHeat: {},
    systemTypes: {},
    refrigerants: {}
  };
  Object.entries(countsByConfig).forEach(([configId, counts]) => {
    if (selected && !selected.has(configId)) return;
    COUNT_DIMENSIONS.forEach((dimension) => {
      const target = merged[dimension] as Record<string, number>;
      Object.entries(counts[dimension]).forEach(([key, count]) => {
        target[key] = (target[key] ?? 0) + count;
      });
    });
  });
  return merged;
}

export default function useEquipmentFilters(): EquipmentFiltersModel {
  const fetchedConfigurations = useFetchedConfigurations();
  const systemCombinationsFull = useAppSelector(
    (state) => state.systems.systemCombinationsFull
  );
  const filterUniverseByConfig = useAppSelector(
    (state) => state.systems.filterUniverseByConfig
  );
  const filterOptionCountsByConfig = useAppSelector(
    (state) => state.systems.filterOptionCountsByConfig
  );
  // The PTAC pathway's options are composed server-side per brand/series
  // (features/ptac-selection) rather than from mini-split combinations.
  const isPtac = useIsPtacPathway();
  const ptacOptions = useAppSelector(
    (state) => state.ptacSelection.systems.options
  );

  const allSystemOptions = useMemo<SystemOptionType[]>(() => {
    const configById = new Map<string, ConfigurationType>(
      fetchedConfigurations.map((c) => [c.uniqueId, c])
    );
    const options: SystemOptionType[] = [];
    Object.entries(systemCombinationsFull).forEach(
      ([configId, combinations]) => {
        const config = configById.get(configId);
        if (!config) return;
        combinations.forEach((combination) => {
          const option = combinationToSystemOption(combination, config);
          if (option) options.push(option);
        });
      }
    );
    return isPtac ? [...options, ...ptacOptions] : options;
  }, [systemCombinationsFull, fetchedConfigurations, isPtac, ptacOptions]);

  const selectedConfigIds = useAppSelector(
    (state) => state.ui.equipmentFilters.configIds
  );

  const optionsByConfig = useMemo(() => {
    const byConfig = new Map<string, SystemOptionType[]>();
    allSystemOptions.forEach((option) => {
      const configId = option.configuration.uniqueId;
      const list = byConfig.get(configId);
      if (list) {
        list.push(option);
      } else {
        byConfig.set(configId, [option]);
      }
    });
    return byConfig;
  }, [allSystemOptions]);

  const liveConfigIds = useMemo(
    () => fetchedConfigurations.map((c) => c.uniqueId),
    [fetchedConfigurations]
  );
  // Same reconciliation the filter-state path applies: dead persisted ids
  // (configurations reset) must not empty the scoped universe/counts. The
  // reconciled list is always concrete (never null) so ineligible configs'
  // server universes and counts stay excluded even with no explicit filter.
  const reconciledConfigIds = useMemo(
    () => Array.from(reconcileConfigIds(selectedConfigIds, liveConfigIds)),
    [selectedConfigIds, liveConfigIds]
  );

  const effectiveRawUniverse = useMemo(
    () =>
      buildEffectiveRawUniverse(
        reconciledConfigIds,
        optionsByConfig,
        filterUniverseByConfig
      ),
    [reconciledConfigIds, optionsByConfig, filterUniverseByConfig]
  );

  const optionCounts = useMemo(
    () =>
      mergeFilterOptionCounts(reconciledConfigIds, filterOptionCountsByConfig),
    [reconciledConfigIds, filterOptionCountsByConfig]
  );

  const [universe, filterState, setFilterState] = useSystemListFilterState(
    fetchedConfigurations,
    effectiveRawUniverse
  );

  // Local labels first, then server labels overlay them: a series only
  // present server-side has no loaded product to build a label from, and
  // for shared series the server label wins.
  const condenserSeriesLabels = useMemo(() => {
    const labels = buildCondenserSeriesLabels(allSystemOptions);
    Object.values(filterUniverseByConfig).forEach((serverUniverse) => {
      Object.assign(labels, serverUniverse.condenserSeriesLabels);
    });
    return labels;
  }, [allSystemOptions, filterUniverseByConfig]);

  return {
    allSystemOptions,
    universe,
    filterState,
    setFilterState,
    condenserSeriesLabels,
    optionCounts
  };
}
